import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Upload as UploadIcon,
  FileImage,
  X,
  BadgeCheck,
  AlertTriangle,
  RefreshCcw,
  AlertCircle,
  Image as ImageIcon,
  Trash2,
  Sparkles,
  ListMusic,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import WaveformLoader from '@/components/WaveformLoader';
import LockedOverlay from '@/components/LockedOverlay';
import useSubscription from '@/hooks/useSubscription';
import { toast } from 'sonner';
import {
  hashFile,
  findKnownHashes,
  recordUploaded,
  shouldShowCleanupNudge,
  neverShowCleanupNudge,
} from '@/lib/uploadHistory';

const STATUS_LABEL = {
  queued: 'Queued',
  analyzing: 'Analyzing',
  enriching: 'Enriching',
  done: 'Done',
  unidentified: 'Needs fix',
  error: 'Error',
};

export default function Upload() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLocked } = useSubscription();
  const [files, setFiles] = useState([]); // [{file, preview, id, hash, isDuplicate}]
  const [batchId, setBatchId] = useState(null);
  const [batchItems, setBatchItems] = useState([]);
  const [batchStartedAt, setBatchStartedAt] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Whether we've recorded the post-scan hashes yet (we only want to record
  // them once per batch, on completion — not on every poll tick).
  const [hashesRecorded, setHashesRecorded] = useState(false);
  // Cleanup nudge visibility — opt-out only, persists across sessions.
  const [showCleanupNudge, setShowCleanupNudge] = useState(true);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Count of newly-flagged duplicates among the staged files
  const duplicateCount = useMemo(
    () => files.filter((f) => f.isDuplicate).length,
    [files],
  );

  const addFiles = useCallback(async (incoming) => {
    // Filter to images and create preview/file objects
    const candidates = [];
    for (const f of incoming) {
      if (!f.type.startsWith('image/')) continue;
      candidates.push({
        id: `${f.name}-${f.size}-${f.lastModified}`,
        file: f,
        preview: URL.createObjectURL(f),
      });
    }
    if (candidates.length === 0) return;

    // Hash all candidates in parallel — runs on a worker thread, won't
    // block UI even for 50+ files. Then look up against the upload-history
    // localStorage to mark anything the user has already scanned before.
    const hashes = await Promise.all(candidates.map((c) => hashFile(c.file)));
    const known = findKnownHashes(hashes);

    const annotated = candidates.map((c, i) => ({
      ...c,
      hash: hashes[i],
      isDuplicate: hashes[i] ? known.has(hashes[i]) : false,
    }));

    setFiles((prev) => {
      const seenIds = new Set(prev.map((x) => x.id));
      const seenHashes = new Set(prev.map((x) => x.hash).filter(Boolean));
      // Skip files that are dupes of OTHER staged files too (not just history)
      const fresh = annotated.filter((x) => !seenIds.has(x.id) && !seenHashes.has(x.hash));
      return [...prev, ...fresh];
    });

    // Surface the dupe count to the user as a friendly toast — opt-in,
    // not blocking. They can still scan if they want to.
    const newDupes = annotated.filter((x) => x.isDuplicate).length;
    if (newDupes > 0) {
      toast.warning(
        `${newDupes} ${newDupes === 1 ? 'screenshot was' : 'screenshots were'} already scanned before`,
        { description: 'Tap the orange badge to remove them, or scan anyway.' },
      );
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Quick action: drop every flagged duplicate from the staged batch.
  const removeAllDuplicates = () => {
    const removed = files.filter((f) => f.isDuplicate).length;
    setFiles((prev) => prev.filter((f) => !f.isDuplicate));
    toast.success(`${removed} duplicate${removed === 1 ? '' : 's'} removed from this batch`);
  };

  // Refresh the cleanup-nudge opt-out flag whenever this page mounts.
  useEffect(() => {
    setShowCleanupNudge(shouldShowCleanupNudge());
  }, []);

  // Snap & Seek hand-off: if the URL carries `?batch=<id>` (set by the
  // SnapAndSeekButton after it uploaded the camera capture), hydrate the
  // batch state and let the existing polling flow take over so the user
  // sees identical progress UI regardless of entry point.
  useEffect(() => {
    const incomingBatch = searchParams.get('batch');
    if (!incomingBatch || batchId === incomingBatch) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/scan/batch/${incomingBatch}`);
        if (!active) return;
        setBatchId(incomingBatch);
        setBatchItems(data.items || []);
        setBatchStartedAt(Date.now());
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load your snap batch');
      } finally {
        // Clean the URL so a refresh doesn't re-trigger the hydration.
        setSearchParams({}, { replace: true });
      }
    })();
    return () => { active = false; };
  }, [searchParams]);

  const startScan = async () => {
    if (files.length === 0) {
      toast.error('Add some screenshots first');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f.file, f.file.name));
    try {
      const { data } = await api.post('/scan/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setBatchId(data.batch_id);
      setBatchItems(data.items || []);
      setBatchStartedAt(Date.now());
      toast.success(`Scanning ${data.items?.length || 0} screenshot${data.items?.length === 1 ? '' : 's'}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Poll batch status
  useEffect(() => {
    if (!batchId) return;
    let active = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/scan/batch/${batchId}`);
        if (!active) return;
        setBatchItems(data.items || []);
        const allDone = (data.items || []).every((it) =>
          ['done', 'unidentified', 'error'].includes(it.status)
        );
        if (allDone) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        // soft fail; keep polling
      }
    };
    pollRef.current = setInterval(tick, 1800);
    tick();
    return () => {
      active = false;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [batchId]);

  const reset = () => {
    files.forEach((f) => URL.revokeObjectURL(f.preview));
    setFiles([]);
    setBatchId(null);
    setBatchItems([]);
    setBatchStartedAt(null);
    setHashesRecorded(false);
  };

  // Once the scan completes, persist hashes of the successfully-scanned files
  // so future selections can flag them as duplicates. We use the original
  // `files` array (which still carries the hashes) and match by filename
  // against the batchItems (since the server doesn't echo hashes).
  useEffect(() => {
    if (hashesRecorded) return;
    if (!batchItems.length) return;
    const allDone = batchItems.every((it) =>
      ['done', 'unidentified', 'error'].includes(it.status)
    );
    if (!allDone) return;
    // Record any file whose corresponding batch item ended in 'done' or
    // 'unidentified' (both consumed an upload; only hard 'error' is skipped).
    const successByName = new Map();
    for (const it of batchItems) {
      if (['done', 'unidentified'].includes(it.status)) {
        successByName.set(it.filename, true);
      }
    }
    for (const f of files) {
      if (successByName.get(f.file.name) && f.hash) {
        recordUploaded(f.hash, { filename: f.file.name, size: f.file.size });
      }
    }
    setHashesRecorded(true);
  }, [batchItems, files, hashesRecorded]);

  const progress = (() => {
    if (!batchItems.length) return 0;
    const done = batchItems.filter((it) =>
      ['done', 'unidentified', 'error'].includes(it.status)
    ).length;
    return Math.round((done / batchItems.length) * 100);
  })();
  const isComplete = batchItems.length > 0 && progress === 100;
  const idDoneCount = batchItems.filter((it) => it.status === 'done').length;
  const unidCount = batchItems.filter((it) => it.status === 'unidentified').length;
  const errCount = batchItems.filter((it) => it.status === 'error').length;
  const processingCount = batchItems.filter(
    (it) => !['done', 'unidentified', 'error'].includes(it.status)
  ).length;
  const doneAny = batchItems.length - processingCount;

  // Rough ETA. We store the moment the batch started (see submit handler)
  // and extrapolate from the current identification rate. Only shown while
  // we're actively processing and have enough signal (>= 2 done, > 3s
  // elapsed). Otherwise we omit the ETA line to avoid flashing bogus values.
  const etaText = (() => {
    if (isComplete || !batchStartedAt || doneAny < 2) return null;
    const elapsedMs = Date.now() - batchStartedAt;
    if (elapsedMs < 3000) return null;
    const perItemMs = elapsedMs / doneAny;
    const remainingMs = perItemMs * processingCount;
    if (remainingMs < 4000) return 'A few seconds left';
    const secs = Math.round(remainingMs / 1000);
    if (secs < 60) return `About ${secs}s left`;
    const mins = Math.round(secs / 60);
    return `About ${mins}m left`;
  })();

  // When locked, replace the upload UI with the locked overlay only — no
  // point showing a drop zone the user can't use.
  if (isLocked) {
    return (
      <div className="max-w-3xl mx-auto py-6">
        <LockedOverlay />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
        Scan a crate
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        Drop one or many screenshots. We&apos;ll read them, file them, and build buy links.
      </p>

      {/* Tracklist mode teaser — one image, many tracks. Only shown before a
          scan is running so it doesn't compete for attention with the live
          progress dashboard. */}
      {!batchId && (
        <button
          type="button"
          onClick={() => navigate('/upload/tracklist')}
          data-testid="upload-tracklist-mode-cta"
          className="mt-4 w-full rounded-xl border border-border bg-card hover:border-[hsl(var(--primary))]/50 hover:bg-card/80 transition-colors duration-200 p-3 flex items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]/60"
        >
          <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))]/15 grid place-items-center shrink-0">
            <ListMusic className="w-4 h-4 text-[hsl(var(--primary))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Got a whole tracklist in one image?</div>
            <div className="text-[11px] text-muted-foreground truncate">
              DJ setlists, festival lineups, playlist screenshots — parse up to 30 tracks at once.
            </div>
          </div>
          <span className="text-[11px] text-[hsl(var(--primary))] font-medium shrink-0 hidden sm:inline">
            Upload tracklist snap now →
          </span>
          <span className="text-[11px] text-[hsl(var(--primary))] font-medium shrink-0 sm:hidden">
            Upload →
          </span>
        </button>
      )}

      {/* Drop zone */}
      {!batchId && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          data-testid="upload-dropzone"
          className={`relative mt-6 rounded-2xl border-2 border-dashed p-6 sm:p-10 text-center transition-colors duration-200 noise ${
            isDragging
              ? 'border-[hsl(var(--primary))] bg-white/5'
              : 'border-border bg-card'
          }`}
        >
          <div className="mx-auto w-12 h-12 rounded-full bg-secondary grid place-items-center mb-3">
            <UploadIcon className="w-6 h-6 text-[hsl(var(--primary))]" />
          </div>
          <h3 className="font-display text-lg font-semibold">Drop screenshots to scan</h3>
          <p className="text-sm text-muted-foreground mt-1">
            JPG / PNG / WEBP · 50+ at a time · We never share your images
          </p>
          <div className="mt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="upload-file-input"
              onChange={(e) => {
                if (e.target.files) addFiles(Array.from(e.target.files));
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-file-picker-button"
              className="rounded-xl"
            >
              <FileImage className="w-4 h-4 mr-2" /> Choose files
            </Button>
          </div>

          {files.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground">
                  {files.length} file{files.length === 1 ? '' : 's'} ready
                </div>
                <Button variant="ghost" size="sm" onClick={reset} className="rounded-lg">
                  Clear
                </Button>
              </div>

              {/* Duplicate-detection banner — appears only when at least one
                  staged file matches a hash from a previous successful scan. */}
              {duplicateCount > 0 && (
                <div
                  className="mb-3 rounded-lg border border-[hsl(36_100%_60%)]/40 bg-[hsl(36_100%_60%)]/10 px-3 py-2.5 flex items-start gap-2.5"
                  data-testid="upload-duplicate-banner"
                  role="status"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 text-[hsl(36_100%_60%)] shrink-0" />
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="text-foreground font-medium">
                      {duplicateCount} {duplicateCount === 1 ? 'screenshot was' : 'screenshots were'} already scanned
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      Scanning again will re-process and may create duplicate tracks in your crate.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={removeAllDuplicates}
                    className="shrink-0 h-7 text-xs rounded-md"
                    data-testid="upload-remove-duplicates-button"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove dupes
                  </Button>
                </div>
              )}

              <ul className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-secondary group ${
                      f.isDuplicate ? 'ring-2 ring-[hsl(36_100%_60%)]' : ''
                    }`}
                    data-testid={f.isDuplicate ? 'upload-tile-duplicate' : 'upload-tile'}
                  >
                    <img src={f.preview} alt={f.file.name} className="w-full h-full object-cover" />
                    {f.isDuplicate && (
                      <span
                        className="absolute bottom-1 left-1 right-1 text-[9px] uppercase font-semibold tracking-wide rounded bg-[hsl(36_100%_60%)] text-black text-center py-0.5"
                        title="Already scanned before"
                      >
                        Already scanned
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      aria-label="Remove"
                      className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1 transition-colors duration-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
              <Button
                onClick={startScan}
                disabled={uploading || files.length === 0}
                data-testid="upload-start-scan-button"
                className="mt-5 rounded-xl w-full sm:w-auto"
                size="lg"
              >
                {uploading ? (
                  <>
                    <WaveformLoader size="sm" className="mr-2" /> Uploading…
                  </>
                ) : (
                  <>Start scan →</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Processing dashboard */}
      {batchId && (
        <div className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                {!isComplete && <WaveformLoader />}
                <div className="min-w-0">
                  <div className="font-display font-semibold">
                    {isComplete ? 'Scan complete' : 'Listening to pixels…'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {progress}% \u00b7 {doneAny}/{batchItems.length} scanned
                    {etaText ? ` \u00b7 ${etaText}` : ''}
                  </div>
                </div>
              </div>
              {isComplete && (
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={reset} className="rounded-lg" data-testid="upload-scan-again">
                    <RefreshCcw className="w-4 h-4 mr-1.5" /> Scan more
                  </Button>
                  <Button size="sm" onClick={() => navigate('/crate')} className="rounded-lg" data-testid="upload-go-crate">
                    Go to collection →
                  </Button>
                </div>
              )}
            </div>
            <Progress value={progress} className="h-1.5" />
            {/* Live counts — always visible so users get instant feedback
                on how the scan is going, not only at the very end. */}
            <div className="flex flex-wrap gap-2 mt-3 text-xs" data-testid="upload-progress-counts">
              {idDoneCount > 0 && (
                <Badge variant="secondary" className="font-mono">
                  <BadgeCheck className="w-3 h-3 mr-1" /> {idDoneCount} identified
                </Badge>
              )}
              {processingCount > 0 && !isComplete && (
                <Badge variant="outline" className="font-mono">
                  <Sparkles className="w-3 h-3 mr-1 animate-pulse" />
                  {processingCount} in progress
                </Badge>
              )}
              {unidCount > 0 && (
                <Badge variant="destructive" className="font-mono">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {unidCount} need fix
                </Badge>
              )}
              {errCount > 0 && (
                <Badge variant="outline" className="font-mono">
                  {errCount} error{errCount === 1 ? '' : 's'}
                </Badge>
              )}
            </div>
          </div>

          <ul className="mt-4 space-y-2" data-testid="upload-progress-list">
            {batchItems.map((it) => {
              const preview = it.card_preview || null;
              const isProcessing = !['done', 'unidentified', 'error'].includes(it.status);
              return (
                <li
                  key={it.id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card"
                  data-testid={`upload-item-${it.status}`}
                >
                  <div className="w-10 h-10 shrink-0 rounded-md bg-secondary grid place-items-center overflow-hidden relative">
                    {preview?.album_art_url ? (
                      <img
                        src={preview.album_art_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileImage className={`w-4 h-4 text-muted-foreground ${isProcessing ? 'animate-pulse' : ''}`} />
                    )}
                    {isProcessing && (
                      // Subtle shimmer overlay while this item is still being
                      // OCR'd + enriched. Purely cosmetic; ignored on complete.
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_1.6s_infinite]" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {preview ? (
                      <>
                        <div className="truncate text-sm font-medium">{preview.title || 'Untitled'}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {preview.artist}
                          {preview.genre_bucket ? ` \u00b7 ${preview.genre_bucket}` : ''}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="truncate text-sm">{it.filename}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {it.error || (it.status === 'unidentified' ? 'AI couldn\u2019t ID this one' : isProcessing ? 'Scanning…' : '')}
                        </div>
                      </>
                    )}
                  </div>
                  <StatusBadge status={it.status} />
                </li>
              );
            })}
          </ul>

          {/* Post-scan photo-cleanup nudge.
              Browser sandboxes prevent us from deleting from the user's
              Photos app directly — but we can offer a deep link + a friendly
              reminder so users can clean up the clutter they no longer need.
              The "Don't ask again" preference persists in localStorage. */}
          {isComplete && idDoneCount > 0 && showCleanupNudge && (
            <CleanupNudgeCard
              count={idDoneCount}
              onDismiss={() => setShowCleanupNudge(false)}
              onNeverShow={() => {
                neverShowCleanupNudge();
                setShowCleanupNudge(false);
                toast.success('Got it — we won\'t nudge you again.');
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- CleanupNudgeCard ---------------- *
 * Shown after a successful scan to help users clear screenshot clutter
 * from their Photos app. iOS supports the `photos-redirect://` deep link;
 * we attempt it and gracefully fall back to plain instructions on other
 * platforms. */
function CleanupNudgeCard({ count, onDismiss, onNeverShow }) {
  const handleOpenPhotos = () => {
    // Use a tiny iframe trick to attempt the deep link without navigating
    // away from the app on platforms where the scheme isn't registered.
    try {
      window.location.href = 'photos-redirect://';
    } catch (_) {
      /* nothing else we can do */
    }
    toast(
      'If the Photos app didn\'t open, just open it manually — your screenshots are there.',
    );
  };

  return (
    <div
      className="mt-4 rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4"
      data-testid="upload-cleanup-nudge"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/15 grid place-items-center">
          <ImageIcon className="w-5 h-5 text-[hsl(var(--primary))]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">
            <Sparkles className="inline w-3.5 h-3.5 mr-1 text-[hsl(var(--primary))]" />
            Clean up your Photos library?
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Those {count} screenshot{count === 1 ? ' is' : 's are'} now safely in your GroovLens crate.
            You can free up space by deleting them from your Photos app — we&apos;ll remember which ones you&apos;ve already scanned so you won&apos;t lose track.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleOpenPhotos}
              className="rounded-lg h-8 text-xs"
              data-testid="upload-cleanup-open-photos"
            >
              <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Open Photos
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              className="rounded-lg h-8 text-xs"
              data-testid="upload-cleanup-not-now"
            >
              Not now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onNeverShow}
              className="rounded-lg h-8 text-xs text-muted-foreground hover:text-foreground"
              data-testid="upload-cleanup-never-show"
            >
              Don&apos;t ask again
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
            💡 Coming soon (native app): one-tap auto-move into a GroovLens album, with optional batch delete.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const className = {
    queued: 'bg-secondary text-muted-foreground',
    analyzing: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]',
    enriching: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]',
    done: 'bg-[var(--brand-lime)]/15 text-[var(--brand-lime)]',
    unidentified: 'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]',
    error: 'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]',
  }[status] || 'bg-secondary text-muted-foreground';
  return (
    <span className={`text-[10px] font-mono uppercase rounded-md px-2 py-1 ${className}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
